import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';

import { fetchJson } from '@zapier/toolbox-browser-fetch';

import { DeployKey } from 'app/developer-v3/types/deployKey';
import { ZAPIER_APP_BASE_URL } from 'app/common/constants';
import { useNotify } from 'app/developer-v3/hooks';

import { getQueryKey } from './useQueryActiveDeployKeys';

type Args = DeployKey;

const MUTATION_KEY = 'mutate-delete-deploy-key';
const queryKey = getQueryKey();

function useMutationDeleteDeployKeys(
  options?: UseMutationOptions<
    Record<string, any>,
    Error,
    Args,
    { deployKeys: DeployKey[] }
  >
) {
  const notify = useNotify();

  const queryClient = useQueryClient();

  const mutationKey = MUTATION_KEY;
  const url = new URL('/api/platform/cli/keys', ZAPIER_APP_BASE_URL);

  const mutation = useMutation({
    mutationKey,
    mutationFn: async (args: Args) =>
      fetchJson(url.href, {
        body: JSON.stringify({ key_id: args.id }),
        method: 'DELETE',
      }),
    onMutate: async deployKeyDeleted => {
      // Optimistic update.
      await queryClient.cancelQueries(queryKey);
      const deployKeys = queryClient.getQueryData<DeployKey[]>(queryKey);
      const deployKeysNext = deployKeys.filter(
        ({ id }) => id !== deployKeyDeleted.id
      );
      queryClient.setQueryData(queryKey, deployKeysNext);
      // This builds the context passed over to onError/onSuccess callbacks.
      return { deployKeys };
    },
    onError: (error, _variables, context) => {
      queryClient.setQueryData(queryKey, context.deployKeys);
      notify.failure(error.message, mutationKey);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(queryKey);
      notify.success('Deploy key deleted.');
    },
    ...options,
  });

  return { ...mutation, mutate: mutation.mutateAsync, mutationKey };
}

export default useMutationDeleteDeployKeys;
